import { useMipManageController } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/useMipManageController';
import { MIPManageContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MIPManageContext.ts';
import { TEST_ELEMENT_IDS } from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { ConfirmationModalV3 } from '@dealstack/frontend/shared/components/ConfirmationModal/ConfirmationModalV3';
import {
	useTranslationCommonUi,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import i18n from 'i18next';
import { useContext, useEffect } from 'react';
import { Navigate, Route } from 'react-router-dom';
import { Button, Flex } from '@mantine/core';
import { Investors } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/Investors';
import { InvestorDetailsV2 } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/InvestorDetailsV2';
import { Payments as MIPManagePayments } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Payments/Payments';
import { BankAccounts } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/BankAccounts';
import { BankAccountDetails } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/BankAccountDetails/BankAccountDetails';
import { ProgrammeSettings } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/ProgrammeSettings';
import { SentryRoutes } from '@dealstack/frontend/shared/sentry';
import { WorkflowProgressProvider } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/WorkflowProgress/WorkflowProgressContext';
import { MIP_MANAGE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { mipManagePath } from '@dealstack/frontend/app/pages/Routing/routes.utils.ts';
import { ManageKYCLayout } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/component/ManageKYCLayout';
import { InvestmentProgrammeDocuments } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/InvestmentProgrammeDocuments';
import { InvestmentProgrammeDocumentDetails } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/InvestmentProgrammeDocumentDetails';
import { ManageInvestments } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/ManageInvestments.tsx';
import { InvestmentDetails } from './InvestmentDetails/InvestmentDetails';
import { InvestmentHistoryPage } from './InvestmentDetails/History/InvestmentHistoryPage';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag.ts';
import { MipManageTabsLayout } from './components/MipManageTabsLayout/MipManageTabsLayout.tsx';
import { QuickSwitchBreadcrumbsNav } from '@dealstack/frontend/shared/components/QuickSwitchBreadcrumbsNav/QuickSwitchBreadcrumbsNav.tsx';
import { makeDealPageBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/dealBreadcrumbs.utils.tsx';
import {
	BreadcrumbsProvider,
	useBreadcrumbs,
} from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs.tsx';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { makeMipPageBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/mipBreadcrumbs.utils.tsx';
import { InvestmentVehicleDetails } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestmentVehicleDetails/InvestmentVehicleDetails.tsx';

// Namespaces used by sub-pages (settings, KYC, investor/investment/document details,
// bulk-action modals) that aren't pulled in by the tab pages. Preloading them here means
// the we avoid unnesecary fullpage rerenders.
const SUB_PAGE_NAMESPACES_TO_PRELOAD = [
	I18Namespaces.ADMIN_DASHBOARD,
	I18Namespaces.MIP_CREATE,
	I18Namespaces.CAP_MANAGE,
	I18Namespaces.COMMON_FORM,
	I18Namespaces.DOCUMENT_LIBRARY,
	I18Namespaces.INVESTOR,
];

export const MIPManage = () => {
	const tCommonUi = useTranslationCommonUi();
	const tMipManage = useTranslationMipManage();
	const mipManageController = useMipManageController();
	// The breadcrumb offers only the drawer; the wizard is reached from Deal Details.
	const { deal, deals, dealId, openQuickCreate } = useContext(DealContext);
	const { mipId, mipData } = mipManageController;

	const { value: inPageFeatureFlag } = useDealstackFeatureFlag(
		FEATURE_FLAG.IN_PAGE_BREADCRUMBS_AND_QUICK_SWITCH_MENU
	);

	useEffect(() => {
		void i18n.loadNamespaces(SUB_PAGE_NAMESPACES_TO_PRELOAD);
	}, []);

	const { value: investmentDetailsFeatureFlag, loading: investmentDetailsFeatureFlagLoading } =
		useDealstackFeatureFlag(FEATURE_FLAG.INVESTMENT_DETAILS);

	const {
		value: investmentVehicleDetailsFeatureFlag,
		loading: investmentVehicleDetailsFeatureFlagLoading,
	} = useDealstackFeatureFlag(FEATURE_FLAG.INVESTMENT_VEHICLE_DETAILS);

	const routeIds = {
		dealId: mipManageController.dealId,
		mipId: mipManageController.mipId,
	};
	const investorsFallback = mipManagePath(MIP_MANAGE_ROUTES.INVESTORS, routeIds);
	const investmentsFallback = mipManagePath(MIP_MANAGE_ROUTES.INVESTMENTS, routeIds);

	const baseBreadcrumbs = [
		...makeDealPageBreadcrumbs({
			tCommonUi,
			dealId,
			dealName: deal?.name,
			deals,
			onlyDealDropdown: inPageFeatureFlag,
		}),
		...makeMipPageBreadcrumbs({
			dealId,
			mipName: mipData?.programmeName,
			mipId,
			mips: deal?.mips,
			createNewMip: openQuickCreate,
		}),
	];
	useBreadcrumbs(baseBreadcrumbs, { skipFirst: true });

	// Rendered while the flag is loading (null) so a deep-link doesn't mount-then-vanish;
	// once resolved it either shows the page or redirects to the investments tab.
	const getInvestmentDetailsElement = () => {
		if (investmentDetailsFeatureFlagLoading) {
			return null;
		}
		return investmentDetailsFeatureFlag ? (
			<InvestmentDetails />
		) : (
			<Navigate to={investmentsFallback} replace />
		);
	};

	const getInvestmentVehicleDetailsElement = () => {
		if (investmentVehicleDetailsFeatureFlagLoading) {
			return null;
		}

		return investmentVehicleDetailsFeatureFlag ? (
			<InvestmentVehicleDetails />
		) : (
			<Navigate to={investorsFallback} replace />
		);
	};

	const getBankAccountsElement = () => {
		if (mipManageController.isBankAccountsGateResolving) {
			return null;
		}
		return mipManageController.showBankAccountsTab ? (
			<BankAccounts />
		) : (
			<Navigate to={investorsFallback} replace />
		);
	};

	const getBankAccountDetailsElement = () => {
		if (mipManageController.isBankAccountsGateResolving) {
			return null;
		}
		return mipManageController.showBankAccountsTab ? (
			<BankAccountDetails />
		) : (
			<Navigate to={investorsFallback} replace />
		);
	};

	const getInvestmentHistoryElement = () => {
		if (investmentDetailsFeatureFlagLoading) {
			return null;
		}
		return investmentDetailsFeatureFlag ? (
			<InvestmentHistoryPage />
		) : (
			<Navigate to={investmentsFallback} replace />
		);
	};

	return (
		<MIPManageContext.Provider value={mipManageController}>
			<WorkflowProgressProvider>
				<BreadcrumbsProvider>
					<Flex justify="space-between" align="center" p="1rem 2rem 0">
						<QuickSwitchBreadcrumbsNav />

						<div id="mip-manage-header-slot" />
					</Flex>

					<SentryRoutes>
						<Route
							path={`${MIP_MANAGE_ROUTES.SETTINGS}/*`}
							element={<ProgrammeSettings />}
						/>
						<Route
							path={`${MIP_MANAGE_ROUTES.KYC_PAGE}/*`}
							element={<ManageKYCLayout />}
						/>
						<Route
							path={MIP_MANAGE_ROUTES.DOCUMENT_DETAILS}
							element={<InvestmentProgrammeDocumentDetails />}
						/>

						<Route element={<MipManageTabsLayout />}>
							<Route path={MIP_MANAGE_ROUTES.INVESTORS} element={<Investors />} />
							<Route
								path={MIP_MANAGE_ROUTES.INVESTOR_DETAILS}
								element={<InvestorDetailsV2 />}
							/>
							<Route
								path={MIP_MANAGE_ROUTES.INVESTMENTS}
								element={<ManageInvestments />}
							/>
							<Route
								path={MIP_MANAGE_ROUTES.INVESTMENT_DETAILS}
								element={getInvestmentDetailsElement()}
							/>
							<Route
								path={MIP_MANAGE_ROUTES.INVESTMENT_VEHICLE_DETAILS}
								element={getInvestmentVehicleDetailsElement()}
							/>
							<Route
								path={MIP_MANAGE_ROUTES.INVESTMENT_HISTORY}
								element={getInvestmentHistoryElement()}
							/>
							<Route
								path={MIP_MANAGE_ROUTES.DOCUMENTS}
								element={<InvestmentProgrammeDocuments />}
							/>
							<Route
								path={`${MIP_MANAGE_ROUTES.KYC}/*`}
								element={<ManageKYCLayout />}
							/>
							<Route
								path={MIP_MANAGE_ROUTES.PAYMENTS}
								element={<MIPManagePayments />}
							/>
							<Route
								path={MIP_MANAGE_ROUTES.BANK_ACCOUNTS}
								element={getBankAccountsElement()}
							/>
							<Route
								path={MIP_MANAGE_ROUTES.BANK_ACCOUNT_DETAILS}
								element={getBankAccountDetailsElement()}
							/>
						</Route>

						<Route path="*" element={<Navigate to={investorsFallback} replace />} />
					</SentryRoutes>
				</BreadcrumbsProvider>
			</WorkflowProgressProvider>

			{mipManageController.showMIPManageCongratulationsModal && (
				<ConfirmationModalV3
					title={tMipManage('confirmation_modal.title')}
					description={tMipManage('confirmation_modal.description')}
					children={
						<Flex>
							<Button
								size="md"
								variant="subtle"
								onClick={
									mipManageController.markMIPManageCongratulationsModalAsSeen
								}
								data-testid={
									TEST_ELEMENT_IDS.MIP_MANAGE_SEND_INVITATION_MODAL_CONFIRM
								}
							>
								{tMipManage('confirmation_modal.review_button')}
							</Button>
						</Flex>
					}
				/>
			)}
		</MIPManageContext.Provider>
	);
};
